/**
 * MindMap — the Interactive Guide's horizontal Color → Growth form → Coral
 * tree. Supports drag-to-pan, wheel/buttons zoom, an expand-to-fullscreen
 * mode, and clickable nodes: color/growth nodes filter the catalog, coral
 * nodes open the editable detail pop-up. Geometry comes from the pure
 * `buildMindMapLayout`; this component only renders + handles interaction.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { COLOR_HEX } from './catalog';
import type { MindMapLayout, MindMapNode } from './mindmapLayout';
import type { ColorTag, GrowthForm } from './types';

export interface MindMapProps {
  readonly layout: MindMapLayout;
  readonly onColorClick: (color: ColorTag) => void;
  readonly onGrowthClick: (form: GrowthForm) => void;
  readonly onCoralClick: (coralId: string) => void;
}

function edgePath(from: MindMapNode, to: MindMapNode): string {
  const x1 = from.x + from.w;
  const y1 = from.y + from.h / 2;
  const x2 = to.x;
  const y2 = to.y + to.h / 2;
  const mx = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
}

export function MindMap({
  layout,
  onColorClick,
  onGrowthClick,
  onCoralClick,
}: MindMapProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [scale, setScale] = useState(0.85);
  const [tx, setTx] = useState(16);
  const [ty, setTy] = useState(8);
  const panning = useRef<{ x: number; y: number } | null>(null);

  const nodeById = useMemo(() => {
    const m = new Map<string, MindMapNode>();
    for (const n of layout.nodes) m.set(n.id, n);
    return m;
  }, [layout]);

  const clampScale = (s: number) => Math.min(6, Math.max(0.2, s));

  // Non-passive wheel listener so we can zoom around the cursor without
  // the page scrolling.
  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      setScale((s) => {
        const next = clampScale(s * factor);
        const k = next / s;
        setTx((x) => cx - (cx - x) * k);
        setTy((y) => cy - (cy - y) * k);
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Esc exits fullscreen.
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const zoomButton = (factor: number) => {
    const el = surfaceRef.current;
    const w = el?.clientWidth ?? 600;
    const h = el?.clientHeight ?? 400;
    setScale((s) => {
      const next = clampScale(s * factor);
      const k = next / s;
      setTx((x) => w / 2 - (w / 2 - x) * k);
      setTy((y) => h / 2 - (h / 2 - y) * k);
      return next;
    });
  };

  const resetView = () => {
    setScale(0.85);
    setTx(16);
    setTy(8);
  };

  const beginPan = (e: React.PointerEvent) => {
    panning.current = { x: e.clientX, y: e.clientY };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const movePan = (e: React.PointerEvent) => {
    if (!panning.current) return;
    const dx = e.clientX - panning.current.x;
    const dy = e.clientY - panning.current.y;
    panning.current = { x: e.clientX, y: e.clientY };
    setTx((x) => x + dx);
    setTy((y) => y + dy);
  };
  const endPan = () => {
    panning.current = null;
  };

  const handleNodeClick = (node: MindMapNode) => {
    if (node.kind === 'color') onColorClick(node.color);
    else if (node.kind === 'growth' && node.growthForm) onGrowthClick(node.growthForm);
    else if (node.kind === 'coral' && node.coralId) onCoralClick(node.coralId);
  };

  const nodeClasses = (node: MindMapNode): string => {
    if (node.kind === 'coral') {
      return 'bg-bg-elevated text-text-1 ring-1 ring-surface-stroke hover:ring-accent/60';
    }
    if (node.kind === 'growth') {
      return 'bg-accent-cyan/10 text-accent-cyan ring-1 ring-accent-cyan/40 hover:ring-accent-cyan font-semibold';
    }
    return 'text-white font-bold ring-1 ring-black/30';
  };

  return (
    <div
      ref={wrapRef}
      className={
        fullscreen
          ? 'fixed inset-0 z-[70] bg-bg-base'
          : 'relative h-full w-full rounded-xl bg-bg-mid/40 ring-1 ring-surface-stroke'
      }
    >
      {/* Toolbar */}
      <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-lg bg-bg-elevated/90 p-1 ring-1 ring-surface-stroke backdrop-blur">
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => zoomButton(1 / 1.2)}
          className="rounded px-2 py-1 text-text-2 hover:bg-surface-glass hover:text-text-1"
        >
          −
        </button>
        <button
          type="button"
          aria-label="Reset view"
          onClick={resetView}
          className="rounded px-2 py-1 text-xs text-text-2 hover:bg-surface-glass hover:text-text-1"
        >
          {Math.round(scale * 100)}%
        </button>
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => zoomButton(1.2)}
          className="rounded px-2 py-1 text-text-2 hover:bg-surface-glass hover:text-text-1"
        >
          +
        </button>
        <span className="mx-0.5 h-4 w-px bg-surface-stroke" />
        <button
          type="button"
          aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          onClick={() => setFullscreen((f) => !f)}
          className="rounded px-2 py-1 text-text-2 hover:bg-surface-glass hover:text-text-1"
        >
          {fullscreen ? 'Exit' : '⛶ Full'}
        </button>
      </div>

      {/* Hint */}
      <div className="pointer-events-none absolute left-3 top-3 z-10 max-w-[60%] rounded-lg bg-bg-elevated/80 px-2.5 py-1.5 text-[11px] text-text-3 ring-1 ring-surface-stroke backdrop-blur">
        Color → Growth form → Coral. Click a node to filter or open it.
        Scroll to zoom, drag to pan.
      </div>

      <div
        ref={surfaceRef}
        className="h-full w-full touch-none overflow-hidden"
        style={{ cursor: panning.current ? 'grabbing' : 'grab' }}
        onPointerDown={beginPan}
        onPointerMove={movePan}
        onPointerUp={endPan}
        onPointerLeave={endPan}
      >
        {layout.nodes.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-text-3">
            No corals match the current filters.
          </div>
        ) : (
          <svg width="100%" height="100%" role="presentation">
            <g transform={`translate(${tx} ${ty}) scale(${scale})`}>
              {layout.edges.map((edge) => {
                const from = nodeById.get(edge.from);
                const to = nodeById.get(edge.to);
                if (!from || !to) return null;
                return (
                  <path
                    key={edge.id}
                    d={edgePath(from, to)}
                    fill="none"
                    stroke="rgba(140,175,210,0.35)"
                    strokeWidth={1.5}
                  />
                );
              })}
              {layout.nodes.map((node) => (
                <foreignObject
                  key={node.id}
                  x={node.x}
                  y={node.y}
                  width={node.w}
                  height={node.h}
                >
                  <button
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => handleNodeClick(node)}
                    title={node.label}
                    className={`flex h-full w-full items-center justify-center rounded-lg px-2 text-center text-xs leading-tight transition-shadow ${nodeClasses(node)}`}
                    style={
                      node.kind === 'color'
                        ? { backgroundColor: COLOR_HEX[node.color] }
                        : undefined
                    }
                  >
                    <span className="line-clamp-2">{node.label}</span>
                  </button>
                </foreignObject>
              ))}
            </g>
          </svg>
        )}
      </div>
    </div>
  );
}
